/**
 * 个人中心页面：展示用户信息、算力与快捷入口。
 * 主账号可查看和管理子账号。
 */

import { useState, type ReactElement } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Building2,
  Crown,
  CreditCard,
  History,
  Lock,
  LogOut,
  Server,
  Shield,
  User,
  UserCheck,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ClientPlusManagement } from '@/components/ClientPlusManagement'
import { ClientSeatManagement } from '@/components/ClientSeatManagement'
import { SubAccountManagement } from '@/components/SubAccountManagement'
import { cn } from '@/lib/utils'
import { resetPassword as resetCurrentUserPassword } from '@/services/authService'
import { useAuthStore } from '@/stores'
import type { ComputeType } from '@/types/auth'

const MIN_PASSWORD_LENGTH = 6 as const

/**
 * 脱敏手机号展示。
 * @param phone 手机号
 * @returns 脱敏后的手机号
 */
function maskPhone(phone: string): string {
  if (phone.length < 7) {
    return phone
  }
  const prefix: string = phone.slice(0, 3)
  const suffix: string = phone.slice(-4)
  return `${prefix}****${suffix}`
}

/**
 * 获取算力类型展示文案
 * @param computeType 算力类型
 * @returns 展示文案
 */
function getComputeTypeLabel(computeType: ComputeType | undefined): string {
  return computeType === 'exclusive' ? '专享算力' : '共享算力'
}

/**
 * 格式化会员到期时间。
 * @param value ISO 时间字符串
 * @returns 展示文案
 */
function formatMembershipExpireAt(value: string | undefined): string {
  if (value === undefined || value === '') {
    return '未开通'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '--'
  }
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * 个人中心页面组件。
 * @returns 页面 ReactElement
 */
export function ProfilePage(): ReactElement {
  const navigate = useNavigate()

  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState<boolean>(false)
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [isResettingPassword, setIsResettingPassword] = useState<boolean>(false)

  const clearPasswordForm = (): void => {
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleResetPassword = async (): Promise<void> => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      toast.error(`密码至少 ${String(MIN_PASSWORD_LENGTH)} 位`)
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('两次输入的密码不一致')
      return
    }

    setIsResettingPassword(true)
    try {
      await resetCurrentUserPassword({
        newPassword,
        confirmPassword,
      })
      toast.success('密码重置成功')
      setIsPasswordDialogOpen(false)
      clearPasswordForm()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '密码重置失败')
    } finally {
      setIsResettingPassword(false)
    }
  }

  if (user === null) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">个人中心</h1>
          <p className="text-muted-foreground">登录后可查看算力、记录与订单信息。</p>
        </div>

        <div className="rounded-2xl border bg-card p-10">
          <div className="mx-auto max-w-xl text-center">
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <User className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-tight">欢迎来到月汐AI</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              登录后可同步你的生图记录、充值记录与套餐权益。
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button asChild size="lg">
                <Link to="/login">登录 / 注册</Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <Link to="/workflows">先逛逛工作流</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const computeType = user.company?.computeType ?? 'shared'
  const isExclusive = computeType === 'exclusive'
  const membershipExpireAt = formatMembershipExpireAt(user.company?.exclusiveExpireAt)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">个人中心</h1>
          <p className="text-muted-foreground">管理你的账户、套餐与算力。</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={(): void => { setIsPasswordDialogOpen(true); }}
          >
            <Lock className="h-4 w-4" />
            重置密码
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={(): void => {
              logout()
              void navigate('/')
            }}
          >
            <LogOut className="h-4 w-4" />
            退出登录
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-2">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/10 text-primary">
              <span className="text-lg font-bold">{user.username.slice(0, 1)}</span>
            </div>
            <div className="space-y-1">
              <p className="text-lg font-semibold tracking-tight">{user.username}</p>
              <p className="text-sm text-muted-foreground">
                手机号：{user.phone !== null ? maskPhone(user.phone) : '--'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium',
                  user.isAdmin
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                    : 'bg-secondary text-secondary-foreground/80'
                )}>
                  {user.isAdmin ? (
                    <>
                      <Crown className="h-3.5 w-3.5" />
                      主账号
                    </>
                  ) : (
                    <>
                      <UserCheck className="h-3.5 w-3.5" />
                      子账号
                    </>
                  )}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-secondary-foreground/80">
                  <Building2 className="h-3.5 w-3.5" />
                  {user.companyName}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-secondary-foreground/80">
                  <Shield className="h-3.5 w-3.5" />
                  手机号已绑定
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">账户算力</p>
                <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400">
                  {getComputeTypeLabel(computeType)}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {user.company?.balance ?? 0}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border/50 pt-3">
                <span className="text-xs text-muted-foreground">算力不足？</span>
                <Button asChild size="sm" variant="ghost" className="h-6 px-2 text-xs">
                  <Link to="/pricing">去充值</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">算力类型</p>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    isExclusive
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-secondary text-muted-foreground'
                  )}
                >
                  {isExclusive ? '专享' : '共享'}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Server className="h-5 w-5 text-emerald-500" />
                <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {getComputeTypeLabel(computeType)}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border/50 pt-3">
                <span className="text-xs text-muted-foreground">
                  会员到期：{membershipExpireAt}
                </span>
                {isExclusive ? (
                  <Button asChild size="sm" variant="ghost" className="h-6 px-2 text-xs">
                    <Link to="/exclusive-compute">管理</Link>
                  </Button>
                ) : (
                  <Button asChild size="sm" variant="ghost" className="h-6 px-2 text-xs">
                    <Link to="/workflows">去生图</Link>
                  </Button>
                )}
              </div>
            </div>
            <div className="rounded-2xl bg-secondary/50 p-4">
              <p className="text-xs font-medium text-muted-foreground">快捷操作</p>
              <div className="mt-3 flex flex-col gap-2">
                <Button asChild size="sm">
                  <Link to="/pricing">去充值</Link>
                </Button>
                <Button asChild size="sm" variant="secondary">
                  <Link to="/workflows">去生图</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold tracking-tight">快捷入口</h2>
          <div className="mt-4 grid gap-3">
            {isExclusive ? (
              <Link
                to="/exclusive-compute"
                className={cn(
                  'flex items-center justify-between rounded-xl border bg-background px-4 py-3 transition-colors',
                  'hover:bg-secondary/60'
                )}
              >
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  <Server className="h-4 w-4 text-primary" />
                  专享算力
                </span>
                <span className="text-xs text-muted-foreground">管理</span>
              </Link>
            ) : null}
            <Link
              to="/records/generations"
              className={cn(
                'flex items-center justify-between rounded-xl border bg-background px-4 py-3 transition-colors',
                'hover:bg-secondary/60'
              )}
            >
              <span className="inline-flex items-center gap-2 text-sm font-medium">
                <History className="h-4 w-4 text-primary" />
                生图记录
              </span>
              <span className="text-xs text-muted-foreground">查看</span>
            </Link>

            <Link
              to="/records/recharges"
              className={cn(
                'flex items-center justify-between rounded-xl border bg-background px-4 py-3 transition-colors',
                'hover:bg-secondary/60'
              )}
            >
              <span className="inline-flex items-center gap-2 text-sm font-medium">
                <CreditCard className="h-4 w-4 text-primary" />
                充值记录
              </span>
              <span className="text-xs text-muted-foreground">查看</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 子账号管理 - 仅主账号可见 */}
      {user.isAdmin && <ClientSeatManagement />}
      {user.isAdmin && <ClientPlusManagement />}
      {user.isAdmin && <SubAccountManagement />}

      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={(open): void => {
          setIsPasswordDialogOpen(open)
          if (!open) {
            clearPasswordForm()
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>重置密码</DialogTitle>
            <DialogDescription>
              修改后新密码会立即生效，当前登录状态保持不变。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">新密码</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="请输入新密码"
                value={newPassword}
                onChange={(event): void => { setNewPassword(event.target.value); }}
                disabled={isResettingPassword}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">确认新密码</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="请再次输入新密码"
                value={confirmPassword}
                onChange={(event): void => { setConfirmPassword(event.target.value); }}
                disabled={isResettingPassword}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={(): void => { setIsPasswordDialogOpen(false); }}
              disabled={isResettingPassword}
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={(): void => {
                void handleResetPassword()
              }}
              disabled={isResettingPassword}
            >
              {isResettingPassword ? '提交中...' : '确认重置'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
